export const integrationStats = {
  connectedSources: 3,
  leadsTodayViaApi: 47,
  failedWebhooks: 2,
  lastSync: "2 mins ago",
};

export type IntegrationStatus = "connected" | "not_connected" | "error";

export interface Integration {
  id: string;
  name: string;
  type: string;
  status: IntegrationStatus;
  logoBg: string;
  logoLetters: string;
  leadsToday?: number;
  lastSync?: string;
  method?: string;
  errorMessage?: string;
  lastAttempt?: string;
  description?: string;
  apiKey?: string;
  apiSecret?: string;
  accountId?: string;
  syncFrequency?: string;
  autoAssign?: boolean;
  webhookUrl?: string;
  webhookSecret?: string;
  fieldMapping?: { sourceField: string; crmField: string }[];
}

export const integrations: Integration[] = [
  {
    id: "indiamart",
    name: "IndiaMart",
    type: "B2B Marketplace",
    status: "connected",
    logoBg: "bg-orange-500",
    logoLetters: "IM",
    leadsToday: 18,
    lastSync: "2 mins ago",
    method: "REST API + Webhook",
    apiKey: "im_api_****-****-****-4821",
    apiSecret: "im_sec_****-****-****-a7b3",
    accountId: "IND-2024-PROPMAX",
    syncFrequency: "Real-time",
    autoAssign: true,
    webhookUrl: "https://api.crmpro.in/webhooks/indiamart/prop-2024-a1b2",
    webhookSecret: "whsec_****-****-****-c3d9",
    fieldMapping: [
      { sourceField: "sender_name", crmField: "Lead Name" },
      { sourceField: "mobile", crmField: "Phone" },
      { sourceField: "email", crmField: "Email" },
      { sourceField: "query_message", crmField: "Notes" },
      { sourceField: "city", crmField: "City" },
      { sourceField: "product", crmField: "Source Notes" },
    ],
  },
  {
    id: "justdial",
    name: "JustDial",
    type: "Local Business Directory",
    status: "connected",
    logoBg: "bg-blue-500",
    logoLetters: "JD",
    leadsToday: 12,
    lastSync: "5 mins ago",
    method: "Webhook Push",
    apiKey: "jd_api_****-****-****-f821",
    apiSecret: "jd_sec_****-****-****-e4k9",
    accountId: "JD-2024-PROPMAX",
    syncFrequency: "Every 5 mins",
    autoAssign: true,
    webhookUrl: "https://api.crmpro.in/webhooks/justdial/prop-2024-c3d5",
    webhookSecret: "whsec_****-****-****-g7h2",
    fieldMapping: [
      { sourceField: "caller_name", crmField: "Lead Name" },
      { sourceField: "phone", crmField: "Phone" },
      { sourceField: "city", crmField: "City" },
      { sourceField: "requirement", crmField: "Notes" },
    ],
  },
  {
    id: "tradeindia",
    name: "TradeIndia",
    type: "B2B Trade Portal",
    status: "error",
    logoBg: "bg-red-500",
    logoLetters: "TI",
    errorMessage: "Webhook URL unreachable — check firewall and DNS settings",
    lastAttempt: "1 hour ago",
    method: "Webhook Push",
    apiKey: "ti_api_****-****-****-9m4p",
    apiSecret: "ti_sec_****-****-****-x2n7",
    accountId: "TI-2024-PROPMAX",
    syncFrequency: "Every 15 mins",
    autoAssign: false,
    webhookUrl: "https://api.crmpro.in/webhooks/tradeindia/prop-2024-e7f8",
    webhookSecret: "whsec_****-****-****-j1k5",
    fieldMapping: [
      { sourceField: "buyer_name", crmField: "Lead Name" },
      { sourceField: "mobile", crmField: "Phone" },
      { sourceField: "email", crmField: "Email" },
      { sourceField: "message", crmField: "Notes" },
    ],
  },
  {
    id: "whatsapp",
    name: "WhatsApp Business",
    type: "Meta Cloud API",
    status: "not_connected",
    logoBg: "bg-green-500",
    logoLetters: "WA",
    description:
      "Capture WhatsApp inquiries as leads and enable in-CRM messaging via the Meta Cloud API",
  },
  {
    id: "website-forms",
    name: "Website Forms",
    type: "Webhook / Embed",
    status: "not_connected",
    logoBg: "bg-purple-500",
    logoLetters: "WF",
    description:
      "Embed a form or use webhook URLs to capture website inquiries automatically",
  },
  {
    id: "social-media",
    name: "Social Media Leads",
    type: "Meta Leads API",
    status: "not_connected",
    logoBg: "bg-pink-500",
    logoLetters: "SM",
    description:
      "Import Facebook and Instagram lead gen form submissions via the Meta Leads API",
  },
];

export const telephonyIntegrations: Integration[] = [
  {
    id: "exotel",
    name: "Exotel",
    type: "Cloud Telephony — India",
    status: "not_connected",
    logoBg: "bg-blue-900",
    logoLetters: "EX",
    description:
      "Most popular Indian cloud telephony. Click-to-call, auto call logs, recordings.",
  },
  {
    id: "knowlarity",
    name: "Knowlarity",
    type: "Cloud Telephony — India",
    status: "not_connected",
    logoBg: "bg-orange-500",
    logoLetters: "KN",
    description:
      "Enterprise telephony with IVR, call recording and real-time analytics.",
  },
  {
    id: "myoperator",
    name: "MyOperator",
    type: "Cloud Telephony — India",
    status: "not_connected",
    logoBg: "bg-teal-500",
    logoLetters: "MO",
    description:
      "Simple cloud call center with WhatsApp integration and call tracking.",
  },
];

export interface IntegrationLogEntry {
  time: string;
  source: string;
  sourceColor: string;
  event: string;
  eventIcon: string;
  eventIconColor: string;
  leadDetail: string;
  status: "success" | "error";
}

export const integrationActivityLog: IntegrationLogEntry[] = [
  {
    time: "2 mins ago",
    source: "IndiaMart",
    sourceColor: "bg-orange-100 text-orange-700",
    event: "Lead Received",
    eventIcon: "ri-user-add-line",
    eventIconColor: "text-green-600",
    leadDetail: "Vikram Mehta +91-9876543210",
    status: "success",
  },
  {
    time: "5 mins ago",
    source: "JustDial",
    sourceColor: "bg-blue-100 text-blue-700",
    event: "Lead Received",
    eventIcon: "ri-user-add-line",
    eventIconColor: "text-green-600",
    leadDetail: "Anita Desai +91-9845123456",
    status: "success",
  },
  {
    time: "8 mins ago",
    source: "IndiaMart",
    sourceColor: "bg-orange-100 text-orange-700",
    event: "Lead Received",
    eventIcon: "ri-user-add-line",
    eventIconColor: "text-green-600",
    leadDetail: "Suresh Nair +91-9712345678",
    status: "success",
  },
  {
    time: "12 mins ago",
    source: "IndiaMart",
    sourceColor: "bg-orange-100 text-orange-700",
    event: "Webhook Ping",
    eventIcon: "ri-link",
    eventIconColor: "text-blue-600",
    leadDetail: "Health check",
    status: "success",
  },
  {
    time: "1 hr ago",
    source: "TradeIndia",
    sourceColor: "bg-red-100 text-red-700",
    event: "Webhook Error",
    eventIcon: "ri-error-warning-line",
    eventIconColor: "text-red-600",
    leadDetail: "Connection timeout",
    status: "error",
  },
  {
    time: "1 hr ago",
    source: "TradeIndia",
    sourceColor: "bg-red-100 text-red-700",
    event: "Retry Failed",
    eventIcon: "ri-error-warning-line",
    eventIconColor: "text-red-600",
    leadDetail: "3 attempts failed",
    status: "error",
  },
  {
    time: "2 hrs ago",
    source: "JustDial",
    sourceColor: "bg-blue-100 text-blue-700",
    event: "Lead Received",
    eventIcon: "ri-user-add-line",
    eventIconColor: "text-green-600",
    leadDetail: "Kavita Joshi +91-9632145870",
    status: "success",
  },
  {
    time: "2 hrs ago",
    source: "Website Form",
    sourceColor: "bg-purple-100 text-purple-700",
    event: "Lead Received",
    eventIcon: "ri-user-add-line",
    eventIconColor: "text-green-600",
    leadDetail: "Form submission — Rahul Verma",
    status: "success",
  },
  {
    time: "3 hrs ago",
    source: "IndiaMart",
    sourceColor: "bg-orange-100 text-orange-700",
    event: "Sync Complete",
    eventIcon: "ri-refresh-line",
    eventIconColor: "text-amber-600",
    leadDetail: "8 leads imported",
    status: "success",
  },
  {
    time: "4 hrs ago",
    source: "JustDial",
    sourceColor: "bg-blue-100 text-blue-700",
    event: "Lead Received",
    eventIcon: "ri-user-add-line",
    eventIconColor: "text-green-600",
    leadDetail: "Deepika Singh +91-9415263780",
    status: "success",
  },
];

export const agentWorkload = [
  { name: "Rajesh Kumar", role: "Super Admin", openLeads: 34 },
  { name: "Priya Sharma", role: "Sales Manager", openLeads: 28 },
  { name: "Amit Patel", role: "Sales Executive", openLeads: 19 },
  { name: "Sneha Rao", role: "Viewer", openLeads: 8 },
];

export const activityLog = [
  { time: "Today 10:32 AM", event: "IndiaMart webhook received: 3 new leads", type: "webhook" },
  { time: "Today 10:30 AM", event: "Test lead \"Test User\" created via IndiaMart API", type: "test" },
  { time: "Today 10:15 AM", event: "JustDial sync completed: 2 leads imported", type: "sync" },
  { time: "Today 9:45 AM", event: "TradeIndia webhook failed: 503 Service Unavailable", type: "error" },
  { time: "Today 9:30 AM", event: "IndiaMart auto-assign: 5 leads routed to agents", type: "assign" },
  { time: "Yesterday 6:00 PM", event: "JustDial daily sync: 12 leads imported", type: "sync" },
  { time: "Yesterday 3:20 PM", event: "IndiaMart field mapping updated", type: "config" },
  { time: "Yesterday 11:00 AM", event: "TradeIndia connection test failed: DNS resolution error", type: "error" },
];

export type SourceAccountStatus = "connected" | "error" | "inactive";

export interface SourceAccount {
  id: string;
  name: string;
  identifier: string;
  status: SourceAccountStatus;
  leadsToday: number;
  lastSync: string;
  method: string;
  apiKey?: string;
  apiSecret?: string;
  webhookUrl?: string;
  webhookSecret?: string;
  syncFrequency?: string;
  autoAssign?: boolean;
  fieldMapping?: { sourceField: string; crmField: string }[];
}

export type SourceContainerStatus = "all_active" | "has_error" | "not_connected";

export interface LeadSourceContainer {
  id: string;
  name: string;
  logoBg: string;
  logoLetters: string;
  iconBg: string;
  iconText: string;
  accounts: SourceAccount[];
  totalLeadsToday: number;
  statusSummary: SourceContainerStatus;
}

export const leadSourceContainers: LeadSourceContainer[] = [
  {
    id: "indiamart",
    name: "IndiaMart",
    logoBg: "bg-orange-500",
    logoLetters: "IM",
    iconBg: "bg-orange-100",
    iconText: "text-orange-600",
    totalLeadsToday: 30,
    statusSummary: "all_active",
    accounts: [
      {
        id: "indiamart-mumbai",
        name: "IndiaMart — Mumbai Office",
        identifier: "API ID: IND-2024-MUM",
        status: "connected",
        leadsToday: 18,
        lastSync: "2 mins ago",
        method: "REST API + Webhook",
        apiKey: "im_api_****-****-****-4821",
        apiSecret: "im_sec_****-****-****-a7b3",
        webhookUrl: "https://api.crmpro.in/webhooks/indiamart/mum-2024-a1b2",
        webhookSecret: "whsec_****-****-****-c3d9",
        syncFrequency: "Real-time",
        autoAssign: true,
        fieldMapping: [
          { sourceField: "sender_name", crmField: "Lead Name" },
          { sourceField: "mobile", crmField: "Phone" },
          { sourceField: "email", crmField: "Email" },
          { sourceField: "query_message", crmField: "Notes" },
          { sourceField: "city", crmField: "City" },
          { sourceField: "product", crmField: "Source Notes" },
        ],
      },
      {
        id: "indiamart-pune",
        name: "IndiaMart — Pune Office",
        identifier: "API ID: IND-2024-PUN",
        status: "connected",
        leadsToday: 12,
        lastSync: "5 mins ago",
        method: "REST API + Webhook",
        apiKey: "im_api_****-****-****-p739",
        apiSecret: "im_sec_****-****-****-q4x2",
        webhookUrl: "https://api.crmpro.in/webhooks/indiamart/pun-2024-d8e5",
        webhookSecret: "whsec_****-****-****-f1g9",
        syncFrequency: "Real-time",
        autoAssign: true,
        fieldMapping: [
          { sourceField: "sender_name", crmField: "Lead Name" },
          { sourceField: "mobile", crmField: "Phone" },
          { sourceField: "email", crmField: "Email" },
          { sourceField: "query_message", crmField: "Notes" },
          { sourceField: "city", crmField: "City" },
        ],
      },
    ],
  },
  {
    id: "justdial",
    name: "JustDial",
    logoBg: "bg-blue-500",
    logoLetters: "JD",
    iconBg: "bg-blue-100",
    iconText: "text-blue-600",
    totalLeadsToday: 12,
    statusSummary: "all_active",
    accounts: [
      {
        id: "justdial-mumbai",
        name: "JustDial — Mumbai",
        identifier: "Webhook ID: JD-WH-MUM-2024",
        status: "connected",
        leadsToday: 8,
        lastSync: "3 mins ago",
        method: "Webhook Push",
        apiKey: "jd_api_****-****-****-f821",
        apiSecret: "jd_sec_****-****-****-e4k9",
        webhookUrl: "https://api.crmpro.in/webhooks/justdial/mum-2024-c3d5",
        webhookSecret: "whsec_****-****-****-g7h2",
        syncFrequency: "Every 5 mins",
        autoAssign: true,
        fieldMapping: [
          { sourceField: "caller_name", crmField: "Lead Name" },
          { sourceField: "phone", crmField: "Phone" },
          { sourceField: "city", crmField: "City" },
          { sourceField: "requirement", crmField: "Notes" },
        ],
      },
      {
        id: "justdial-pune",
        name: "JustDial — Pune",
        identifier: "Webhook ID: JD-WH-PUN-2024",
        status: "connected",
        leadsToday: 4,
        lastSync: "8 mins ago",
        method: "Webhook Push",
        apiKey: "jd_api_****-****-****-k5m8",
        apiSecret: "jd_sec_****-****-****-p2n4",
        webhookUrl: "https://api.crmpro.in/webhooks/justdial/pun-2024-h9j1",
        webhookSecret: "whsec_****-****-****-a3b7",
        syncFrequency: "Every 5 mins",
        autoAssign: true,
        fieldMapping: [
          { sourceField: "caller_name", crmField: "Lead Name" },
          { sourceField: "phone", crmField: "Phone" },
          { sourceField: "city", crmField: "City" },
        ],
      },
    ],
  },
  {
    id: "tradeindia",
    name: "TradeIndia",
    logoBg: "bg-red-500",
    logoLetters: "TI",
    iconBg: "bg-red-100",
    iconText: "text-red-600",
    totalLeadsToday: 0,
    statusSummary: "has_error",
    accounts: [
      {
        id: "tradeindia-main",
        name: "TradeIndia — Main",
        identifier: "Webhook ID: TI-WH-2024-MAIN",
        status: "error",
        leadsToday: 0,
        lastSync: "Failed 1 hr ago",
        method: "Webhook Push",
        apiKey: "ti_api_****-****-****-9m4p",
        apiSecret: "ti_sec_****-****-****-x2n7",
        webhookUrl: "https://api.crmpro.in/webhooks/tradeindia/main-2024-e7f8",
        webhookSecret: "whsec_****-****-****-j1k5",
        syncFrequency: "Every 15 mins",
        autoAssign: false,
        fieldMapping: [
          { sourceField: "buyer_name", crmField: "Lead Name" },
          { sourceField: "mobile", crmField: "Phone" },
          { sourceField: "email", crmField: "Email" },
          { sourceField: "message", crmField: "Notes" },
        ],
      },
    ],
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    logoBg: "bg-green-500",
    logoLetters: "WA",
    iconBg: "bg-green-100",
    iconText: "text-green-600",
    totalLeadsToday: 0,
    statusSummary: "not_connected",
    accounts: [],
  },
  {
    id: "website-forms",
    name: "Website Forms",
    logoBg: "bg-purple-500",
    logoLetters: "WF",
    iconBg: "bg-purple-100",
    iconText: "text-purple-600",
    totalLeadsToday: 0,
    statusSummary: "not_connected",
    accounts: [],
  },
  {
    id: "social-media",
    name: "Social Media",
    logoBg: "bg-pink-500",
    logoLetters: "SM",
    iconBg: "bg-pink-100",
    iconText: "text-pink-600",
    totalLeadsToday: 0,
    statusSummary: "not_connected",
    accounts: [],
  },
];